import { type JSX, Show } from "solid-js";
import { cn } from "@gymlog/shared";

interface RouteTransitionProps {
  /**
   * Re-keys the wrapper so the entrance animation re-fires on navigation.
   * Pass the route pathname (without volatile search params) to avoid
   * re-triggering on query-string-only changes.
   */
  routeKey: string;
  class?: string;
  children: JSX.Element;
}

/**
 * Entrance animation shared by every in-app route (canon: the profile page
 * wraps its content in `animate-slide-in-up`). The content of the `<Outlet>`
 * slides up + fades in on mount and re-fires on each navigation, so every page
 * inherits the motion without per-page boilerplate.
 *
 * GPU-first: the underlying `slide-in-up` keyframe animates ONLY `transform`
 * and `opacity` (never layout), so there is no layout shift and no paint storm.
 * `will-change: transform, opacity` is hinted for the duration of the animation
 * and cleared on `animationend` so we never leave a persistent containing block
 * that could interfere with the session carousel / swipe-to-delete transforms.
 *
 * `prefers-reduced-motion` is honoured globally in `global.css` (animations are
 * collapsed to ~0ms while keeping the final state), so reduced-motion users see
 * the content instantly with no movement — and because the keyframe still runs
 * (just instantly), `animationend` still fires and the GPU hint is cleared.
 */
export function RouteTransition(props: RouteTransitionProps) {
  return (
    <Show when={props.routeKey} keyed>
      <div
        class={cn("animate-slide-in-up will-change-[transform,opacity]", props.class)}
        onAnimationEnd={(event) => {
          // Only react to our own entrance keyframe, then drop the GPU hint.
          if (event.target === event.currentTarget) {
            event.currentTarget.style.willChange = "auto";
          }
        }}
      >
        {props.children}
      </div>
    </Show>
  );
}
